// Career Milestone tracker - decides which milestones fire and dedupes so
// the same event doesn't celebrate itself twice for the same user session.
//
// Milestones are keyed by a stable string derived from the underlying entity
// (aligned_id, session_id, analysis_id) + milestone type. Storage lives in
// localStorage so refreshes don't re-fire the same celebration, but a fresh
// user session (new browser / signed-out) will re-see them once.

const STORAGE_KEY = "intuiterview.milestones.seen.v1";

function _readSeen() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function _writeSeen(seen) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(seen));
  } catch {
    /* quota / SSR - ignore */
  }
}

export function hasSeenMilestone(key) {
  if (!key) return true;
  return Boolean(_readSeen()[key]);
}

export function markMilestoneSeen(key) {
  if (!key) return;
  const seen = _readSeen();
  seen[key] = Date.now();
  _writeSeen(seen);
}

// ---------- Milestone builders ----------
// Each builder returns a milestone descriptor OR null if the trigger did
// not qualify. The banner component renders whatever is returned.

/**
 * Resume alignment produced a real improvement.
 * Fires when the match-score delta is >= +5 points.
 */
export function buildResumeOptimizedMilestone(aligned) {
  if (!aligned) return null;
  const before = aligned?.original_scores?.match_score;
  const after = aligned?.updated_scores?.match_score;
  if (typeof before !== "number" || typeof after !== "number") return null;
  const delta = after - before;
  if (delta < 5) return null;
  return {
    key: `resume_optimized:${aligned.aligned_id}`,
    label: "Resume Milestone",
    headline: "Resume Optimized",
    message: "Your AI-enhanced resume significantly increased your match for this role.",
    accent: "gold",
    scoreCards: [
      {
        label: "Resume Match",
        before,
        after,
        delta,
      },
    ],
    cta: { label: "Review Resume", scrollToTestId: "aligned-result" },
    share: {
      title: "Resume Optimized",
      lines: [
        `Resume Match: ${before}% \u2192 ${after}%`,
        `Improvement: +${delta} percentage points`,
      ],
    },
  };
}

/**
 * ATS score reached a "ready" threshold (>= 85) after alignment.
 */
export function buildATSReadyMilestone(aligned) {
  if (!aligned) return null;
  const before = aligned?.original_scores?.ats_score;
  const after = aligned?.updated_scores?.ats_score;
  if (typeof after !== "number" || after < 85) return null;
  return {
    key: `ats_ready:${aligned.aligned_id}`,
    label: "ATS Milestone",
    headline: "ATS Ready",
    message: "Your resume is optimized for applicant tracking systems.",
    accent: "green",
    scoreCards: [
      typeof before === "number"
        ? { label: "ATS Score", before, after, delta: after - before }
        : { label: "ATS Score", after, single: true },
    ],
    cta: { label: "Review Resume", scrollToTestId: "aligned-result" },
    share: {
      title: "ATS Ready",
      lines: [`ATS Score: ${after}%`],
    },
  };
}

/**
 * Official AI Interview or Practice Interview completed. Two variants:
 *   - readiness >= 85 -> "YOU'RE OFFER READY."
 *   - otherwise      -> "Interview Complete"
 */
export function buildInterviewCompleteMilestone(session) {
  if (!session || !session.session_id) return null;
  const report = session.report || {};
  if (report.enrichment_status !== "ready") return null;
  const readiness = typeof report.readiness_score === "number"
    ? report.readiness_score
    : (typeof report.evidence_based_readiness_score === "number"
      ? report.evidence_based_readiness_score
      : null);
  const overall = typeof report.overall_score === "number" ? report.overall_score : null;
  const isIntuiterview = typeof readiness === "number" && readiness >= 85;
  const scoreCards = [];
  if (readiness !== null) {
    scoreCards.push({ label: "Interview Readiness", after: readiness, single: true, denom: 100 });
  }
  if (overall !== null && !isIntuiterview) {
    scoreCards.push({ label: "Overall Score", after: overall, single: true, denom: 100 });
  }
  return {
    key: `interview_complete:${session.session_id}`,
    label: isIntuiterview ? "Career Milestone" : "Interview Milestone",
    headline: isIntuiterview ? "YOU'RE OFFER READY." : "Interview Complete",
    message: isIntuiterview
      ? "You're ready to interview with confidence."
      : "Your Executive Interview Report is ready.",
    accent: isIntuiterview ? "gold" : "emerald",
    confetti: isIntuiterview,
    scoreCards,
    cta: {
      label: isIntuiterview ? "View Executive Report" : "View Report",
      scrollToTestId: "video-report",
    },
    share: {
      title: isIntuiterview ? "Offer Ready" : "Interview Complete",
      lines: [
        readiness !== null ? `Interview Readiness: ${readiness}/100` : null,
        overall !== null ? `Overall Score: ${overall}/100` : null,
      ].filter(Boolean),
    },
  };
}
